import React, { useState, useEffect, useRef } from 'react';
import { useProjects } from '../../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Edit, Trash2, ExternalLink, Plus, X, Clock, User,
  MessageSquare, Paperclip, Upload, Download, FileText, Loader2,
  MapPin, Calendar, Tag, Briefcase, Globe, IndianRupee
} from 'lucide-react';

const STATUS_COLORS = {
  'Prospective': 'bg-slate-100 text-slate-700',
  'Applied': 'bg-blue-100 text-blue-700',
  'Client Responded': 'bg-cyan-100 text-cyan-700',
  'Offered': 'bg-emerald-100 text-emerald-700',
  'On Hold': 'bg-amber-100 text-amber-700',
  'Contract Started': 'bg-green-100 text-green-700',
  'Contract Ended': 'bg-purple-100 text-purple-700',
  'Rejected': 'bg-red-100 text-red-700'
};

const ProjectDetail = ({ projectId, onBack }) => {
  const {
    getProject, updateProject, deleteProject,
    getActivity, addComment,
    getAttachments, uploadAttachment, deleteAttachment, getAttachmentUrl,
    getTags, getSettings, getUsersLookup
  } = useProjects();

  const [project, setProject] = useState(null);
  const [activities, setActivities] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Edit
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // Comment
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  // Tags
  const [allTags, setAllTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Settings data
  const [platforms, setPlatforms] = useState([]);
  const [stages, setStages] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const fileInputRef = useRef(null);

  const loadData = async () => {
    setLoading(true);
    const [proj, acts, atts, tagsData, settingsData, usersData] = await Promise.all([
      getProject(projectId),
      getActivity(projectId),
      getAttachments(projectId),
      getTags(),
      getSettings(),
      getUsersLookup()
    ]);
    setProject(proj);
    setActivities(acts || []);
    setAttachments(atts || []);
    setAllTags(tagsData || []);
    setPlatforms(settingsData?.platforms || []);
    setStages(settingsData?.pipeline_stages || []);
    setAllUsers(usersData || []);
    if (proj) {
      setEditForm({
        title: proj.title || '',
        platform: proj.platform || 'Upwork',
        project_url: proj.project_url || '',
        budget_rate: proj.budget_rate || '',
        client_name: proj.client_name || '',
        client_location: proj.client_location || '',
        cover_letter: proj.cover_letter || '',
        proposal_date: proj.proposal_date || '',
        expected_duration: proj.expected_duration || '',
        tags: proj.tags || [],
        assigned_users: proj.assigned_users || []
      });
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [projectId]);

  const handleStatusChange = async (newStatus) => {
    const result = await updateProject(projectId, { status: newStatus });
    if (result.success) {
      toast.success(`Status changed to ${newStatus}`);
      loadData();
    } else {
      toast.error(result.error);
    }
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    const result = await updateProject(projectId, editForm);
    setEditLoading(false);
    if (result.success) {
      toast.success('Project updated');
      setShowEditDialog(false);
      loadData();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this project permanently?')) return;
    const result = await deleteProject(projectId);
    if (result.success) {
      toast.success('Project deleted');
      onBack();
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setCommentLoading(true);
    const result = await addComment(projectId, newComment);
    setCommentLoading(false);
    if (result.success) {
      setNewComment('');
      const acts = await getActivity(projectId);
      setActivities(acts || []);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadAttachment(projectId, file);
    if (result.success) {
      toast.success('File uploaded');
      const atts = await getAttachments(projectId);
      setAttachments(atts || []);
      const acts = await getActivity(projectId);
      setActivities(acts || []);
    } else {
      toast.error(result.error);
    }
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this file?')) return;
    const result = await deleteAttachment(projectId, attachmentId);
    if (result.success) {
      const atts = await getAttachments(projectId);
      setAttachments(atts || []);
    }
  };

  // Tag helpers for edit form
  const addEditTag = (tagValue) => {
    const tag = (tagValue || tagInput).trim();
    if (tag && !editForm.tags.includes(tag)) {
      setEditForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput('');
    setShowTagSuggestions(false);
  };

  const removeEditTag = (tag) => {
    setEditForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const filteredTagSuggestions = tagInput.trim().length > 0
    ? allTags.filter(t => t.name.toLowerCase().includes(tagInput.toLowerCase()) && !editForm.tags?.includes(t.name)).map(t => t.name)
    : [];

  const toggleUser = (userId) => {
    setEditForm(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(u => u !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const getUserName = (userId) => {
    const u = allUsers.find(u => u.id === userId);
    return u ? (u.full_name || u.username) : userId;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  if (!project) {
    return <div className="text-center py-8 text-slate-500">Project not found</div>;
  }

  return (
    <div className="space-y-4" data-testid="project-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">{project.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{project.platform}</Badge>
              <Badge className={`${STATUS_COLORS[project.status] || 'bg-gray-100 text-gray-700'} border-0`}>{project.status}</Badge>
              {project.project_url && (
                <a href={project.project_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-sm flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />View Posting
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={project.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[180px]" data-testid="status-dropdown">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowEditDialog(true)} data-testid="edit-project-btn">
            <Edit className="w-4 h-4 mr-1" />Edit
          </Button>
          <Button variant="outline" className="text-red-500" onClick={handleDelete}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Activity ({activities.length})</TabsTrigger>
          <TabsTrigger value="attachments">Attachments ({attachments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Project Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-500">Proposal Date:</span>
                  <span className="font-medium">{project.proposal_date}</span>
                </div>
                {project.budget_rate && (
                  <div className="flex items-center gap-2 text-sm">
                    <IndianRupee className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Budget/Rate:</span>
                    <span className="font-medium">{project.budget_rate}</span>
                  </div>
                )}
                {project.expected_duration && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Duration:</span>
                    <span className="font-medium">{project.expected_duration}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-500">Platform:</span>
                  <Badge variant="outline">{project.platform}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Client</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {project.client_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Name:</span>
                    <span className="font-medium">{project.client_name}</span>
                  </div>
                )}
                {project.client_location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Location:</span>
                    <span className="font-medium">{project.client_location}</span>
                  </div>
                )}
                {!project.client_name && !project.client_location && (
                  <p className="text-sm text-slate-400">No client details</p>
                )}
              </CardContent>
            </Card>

            {project.cover_letter && (
              <Card className="md:col-span-2">
                <CardHeader><CardTitle className="text-base">Cover Letter</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{project.cover_letter}</p>
                </CardContent>
              </Card>
            )}

            {project.tags?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Tags</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {project.tags.map((t, i) => (
                      <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <Tag className="w-3 h-3 mr-1" />{t}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle className="text-base">Team</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {project.assigned_users?.map((uid, i) => (
                    <Badge key={i} variant="outline" className="bg-slate-50">
                      <User className="w-3 h-3 mr-1" />{getUserName(uid)}
                    </Badge>
                  ))}
                  {(!project.assigned_users || project.assigned_users.length === 0) && (
                    <p className="text-sm text-slate-400">No team members assigned</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add comment */}
              <div className="flex gap-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment or update..."
                  className="flex-1"
                  rows={2}
                  data-testid="comment-input"
                />
                <Button onClick={handleAddComment} disabled={commentLoading || !newComment.trim()} data-testid="add-comment-btn">
                  {commentLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                </Button>
              </div>

              {/* Timeline */}
              <div className="space-y-3 mt-4">
                {activities.length === 0 ? (
                  <p className="text-center text-slate-400 py-4">No activity yet</p>
                ) : activities.map(activity => (
                  <div key={activity.id} className="flex gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.action === 'COMMENT' ? 'bg-blue-100 text-blue-600' :
                      activity.action === 'STATUS_CHANGED' ? 'bg-amber-100 text-amber-600' :
                      activity.action === 'ATTACHMENT_ADDED' ? 'bg-green-100 text-green-600' :
                      'bg-slate-200 text-slate-600'
                    }`}>
                      {activity.action === 'COMMENT' ? <MessageSquare className="w-4 h-4" /> :
                       activity.action === 'STATUS_CHANGED' ? <Edit className="w-4 h-4" /> :
                       activity.action === 'ATTACHMENT_ADDED' || activity.action === 'ATTACHMENT_REMOVED' ? <Paperclip className="w-4 h-4" /> :
                       <Clock className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">{activity.username}</span>
                        <span className="text-xs text-slate-400">{new Date(activity.timestamp).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">{activity.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attachments">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Attachments</CardTitle>
                <Button size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-attachment-btn">
                  <Upload className="w-4 h-4 mr-1" />Upload File
                </Button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
              </div>
            </CardHeader>
            <CardContent>
              {attachments.length === 0 ? (
                <p className="text-center text-slate-400 py-4">No attachments</p>
              ) : (
                <div className="space-y-2">
                  {attachments.map(att => (
                    <div key={att.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium">{att.filename}</p>
                          <p className="text-xs text-slate-400">{formatSize(att.size)} - by {att.uploaded_by} - {new Date(att.uploaded_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <a href={getAttachmentUrl(projectId, att.id)} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost"><Download className="w-4 h-4" /></Button>
                        </a>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteAttachment(att.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Project Title *</Label>
                <Input value={editForm.title || ''} onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))} data-testid="edit-title" />
              </div>
              <div className="space-y-2">
                <Label>Platform</Label>
                <Select value={editForm.platform || 'Upwork'} onValueChange={(v) => setEditForm(prev => ({ ...prev, platform: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {platforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Proposal Date *</Label>
                <Input type="date" value={editForm.proposal_date || ''} onChange={(e) => setEditForm(prev => ({ ...prev, proposal_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Project URL</Label>
                <Input value={editForm.project_url || ''} onChange={(e) => setEditForm(prev => ({ ...prev, project_url: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Budget / Rate</Label>
                <Input value={editForm.budget_rate || ''} onChange={(e) => setEditForm(prev => ({ ...prev, budget_rate: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Client Name</Label>
                <Input value={editForm.client_name || ''} onChange={(e) => setEditForm(prev => ({ ...prev, client_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Client Location</Label>
                <Input value={editForm.client_location || ''} onChange={(e) => setEditForm(prev => ({ ...prev, client_location: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Expected Duration</Label>
                <Input value={editForm.expected_duration || ''} onChange={(e) => setEditForm(prev => ({ ...prev, expected_duration: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cover Letter</Label>
              <Textarea value={editForm.cover_letter || ''} onChange={(e) => setEditForm(prev => ({ ...prev, cover_letter: e.target.value }))} rows={4} />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="relative">
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
                    placeholder="Add a tag..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEditTag())}
                    onFocus={() => setShowTagSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                  />
                  <Button type="button" variant="outline" onClick={() => addEditTag()}><Plus className="w-4 h-4" /></Button>
                </div>
                {showTagSuggestions && filteredTagSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-32 overflow-y-auto">
                    {filteredTagSuggestions.map((tag, idx) => (
                      <div key={idx} className="px-3 py-1.5 text-sm cursor-pointer hover:bg-blue-50" onMouseDown={(e) => { e.preventDefault(); addEditTag(tag); }}>
                        {tag}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {editForm.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {editForm.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1 bg-blue-50 text-blue-700 border-blue-200">
                      <Tag className="w-3 h-3" />{tag}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeEditTag(tag)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Team Members */}
            <div className="space-y-2">
              <Label>Assign Team Members</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-slate-50">
                {allUsers.map(user => (
                  <Badge
                    key={user.id}
                    variant={editForm.assigned_users?.includes(user.id) ? "default" : "outline"}
                    className={`cursor-pointer ${editForm.assigned_users?.includes(user.id) ? 'bg-blue-600' : ''}`}
                    onClick={() => toggleUser(user.id)}
                  >
                    {user.full_name || user.username}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={editLoading} className="bg-blue-600 hover:bg-blue-700">
              {editLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
